import React, { useEffect, useMemo, useState } from "react";
import { withTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Col, Row } from "reactstrap";
import { get } from "lodash";
import PropTypes from "prop-types";
import {
  Box,
  Stack,
  Collapse,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Button,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";

import DocumentPreviewModal from "Components/Common/DocumentPreviewModal";
import Tooltip from "Components/Common/Tooltip";
import { DocumentName } from "pages/UtilityConsumers/style";
import CommentInput from "Components/Common/CommentInput";
import CommentBox from "Components/Common/CommentBox";
import {
  FIRE_EMERGENCY_CHIEF_FIRE_OFFICER,
  FIRE_SAFETY_CONSUMER,
} from "common/constant";
import AdminDiscussionForBuildingObservation from "common/AdminDiscussionForBuildingObservation";

import { convertCamelTextToCapitalizeText } from "utils/convertCamelTextToCapitalizeText";
import getFileExtension from "utils/getFileExtension";
import { getImageUrl, isAuthorizedUser } from "utils";
import removeExtension from "utils/removeExtension";

import {
  useLazyGetBuildingObservationAdminCommentsByIdQuery,
  useLazyGetBuildingObservationCommentQuery,
  usePostBuildingObservationCommentMutation,
} from "apis/ApplicationRequestComment";
import { usePutCloseBuildingObservationByIdMutation } from "apis/FireSafety/FireSafetyObservation";

import ConsumerResolveDetails from "./ConsumerResolveDetails";
import { getOptionLabel } from "common/enums";
import { convertUTCDateToLocalDate } from "utils/convertUTCDateToLocalDate";

const FireSafetyViewBuildingObservationDetails = ({
  observationDetails,
  index,
  buildingInformationId,
  t,
}) => {
  const {
    id: observationId,
    fireSafetyUser: { roles: currentRoles = [] } = {},
    isApproved,
    isResolved,
    planOfAction,
    resolvedPhotographs,
    otherDetailObservation,
    detailObservation,
    developerNoticeCompliance,
    disableResolveDetailField,
  } = observationDetails;

  const [addComment] = usePostBuildingObservationCommentMutation();

  const [getObservationComment, { data: { data: commentData = [] } = {} }] =
    useLazyGetBuildingObservationCommentQuery();

  const [
    closeBuildingObservation,
    { isLoading: isCloseBuildingObservationLoading },
  ] = usePutCloseBuildingObservationByIdMutation();

  const [
    getAdminObservationComment,
    { data: { data: adminComments = [] } = {} },
  ] = useLazyGetBuildingObservationAdminCommentsByIdQuery(
    buildingInformationId
  );

  const WORK_IN_PROGRESS = 2;
  const [documentToPreview, setDocumentToPreview] = useState(null);
  const [openDiscussion, setOpenDiscussion] = useState(true);
  const [openObservation, setOpenObservation] = useState(false);
  const [resolveImages, setResolveImages] = useState(resolvedPhotographs ?? []);

  const { roles } = useSelector((state) => state.Login);

  const isAdmin = useMemo(() => !roles.includes(FIRE_SAFETY_CONSUMER), [roles]);

  // Role is same as logged in admin OR role is CFO
  const isAuthorizedAdminRole = useMemo(
    () =>
      isAuthorizedUser(roles, currentRoles) ||
      roles.includes(FIRE_EMERGENCY_CHIEF_FIRE_OFFICER),
    [currentRoles]
  );

  const fields = useMemo(
    () => [
      {
        name: "Observation Type",
        key: "observationType.name",
        isRequired: true,
      },
      {
        name: "Tenant Name",
        key: "floorWiseTenant.name",
        isRequired: false,
      },
      {
        name: "Detail observation",
        key: "detailObservation.name",
        isRequired: false,
        isVisible: !!detailObservation?.name,
      },
      {
        name: "Detail observation",
        key: "otherDetailObservation",
        isRequired: false,
        isVisible: !!otherDetailObservation,
      },
    ],
    [planOfAction]
  );
  const consumerResponseDetailField = useMemo(
    () => [
      {
        name: "Corrective Action",
        key: "developerNoticeCompliance.correctiveAction",
        isRequired: false,
        isVisible: isAdmin,
      },
      {
        name: "Compliance Status",
        key: "developerNoticeCompliance.complianceStatus",
        renderField: (value) =>
          getOptionLabel("complianceStatusEnum", value) ?? "-",
        isRequired: false,
        isVisible: isAdmin,
      },
      {
        name: "Target Completion Date",
        key: "developerNoticeCompliance.targetCompletionDate",
        renderField: (value) =>
          value ? convertUTCDateToLocalDate(value).format("DD-MM-YYYY") : "-",
        isRequired: false,
        isVisible: isAdmin && developerNoticeCompliance?.complianceStatus === WORK_IN_PROGRESS,
      },
      {
        name: "Remark",
        key: "developerNoticeCompliance.remark",
        isRequired: false,
        isVisible: isAdmin,
      },
    ],
    [planOfAction]
  );

  const previewButtonHandler = (documentURL) => {
    const fileExtension = getFileExtension(documentURL ?? "");
    const url = getImageUrl(documentURL ?? "");
    setDocumentToPreview({
      documentTypeName: "",
      url,
      documentType: fileExtension,
      documentName: removeExtension(documentURL ?? ""),
    });
  };

  const addNewComment = async (comment, inspectionObservationId) => {
    try {
      await addComment({
        comment,
        inspectionObservationId,
      });
    } catch (error) {}
  };

  const handleApproveResolvePhotographs = async () => {
    try {
      await closeBuildingObservation(observationId);
    } catch (error) {
      console.error(error);
    }
  };

  const getCommentsByObservationId = (comments, id) =>
    comments.find(
      ({ inspectionObservationId }) => id === inspectionObservationId
    )?.inspectionObservationComments || [];

  const handleDiscussionCollapse = () => {
    setOpenDiscussion(!openDiscussion);
  };

  const handleObservationCollapse = () => {
    setOpenObservation(!openObservation);
  };

  useEffect(() => {
    (async () => {
      try {
        if (buildingInformationId) {
          if (isAdmin) {
            await getAdminObservationComment(buildingInformationId);
          }
          await getObservationComment(buildingInformationId);
        }
      } catch (error) {
        console.error(error);
      }
    })();
  }, [buildingInformationId]);

  useEffect(() => setResolveImages(resolvedPhotographs), [resolvedPhotographs]);

  return (
    <>
      {!!documentToPreview && (
        <DocumentPreviewModal
          isOpen
          handleClose={() => setDocumentToPreview(null)}
          {...documentToPreview}
        />
      )}

      <>
        <div
          className="fs-4 fw-semibold mb-3 border-bottom"
          onClick={handleObservationCollapse}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <label className="fw-semibold h5">
              <p className="mb-1">
                {t("Building Observation")} {index + 1} ( Status :{" "}
                {isResolved ? "Approved" : "Pending"} )
              </p>
              <p className="mb-1">
                {t("Observation Added By")}:
                {convertCamelTextToCapitalizeText(
                  observationDetails.fireSafetyUser.userName
                )}{" "}
                ({observationDetails.fireSafetyUser.roleName} of{" "}
                {observationDetails.fireSafetyUser.departmentName})
              </p>
            </label>
            <p>{openObservation ? <ExpandLess /> : <ExpandMore />}</p>
          </Stack>
        </div>
        <Collapse in={openObservation} timeout="auto" unmountOnExit>
          <Row className="d-flex  mb-2">
            {fields.map(({ name, key, isVisible, isRequired, renderField }) => {
              const value = get(observationDetails, key, "");
              if (typeof isVisible !== "boolean" || isVisible) {
                return (
                  <Col key={key} lg={6} className="mb-1">
                    <Row>
                      <Col md={5}>{t(name)}:</Col>
                      <Col
                        md={7}
                        className="fw-semibold"
                        style={{ wordBreak: "break-word" }}
                      >
                        {renderField
                          ? renderField(value)
                          : get(observationDetails, key, null) ??
                            (isRequired ? "-" : "N/A")}
                      </Col>
                    </Row>
                  </Col>
                );
              }
            })}
            {!!observationDetails.observationPhotographs.length && (
              <Row className="d-flex gap-3 flex-wrap mt-1">
                {observationDetails.observationPhotographs.map(
                  ({ photographUrl, name }, index) => (
                    <Col
                      key={index}
                      lg={2}
                      md={4}
                      sm={6}
                      xs={12}
                      className="image-container"
                    >
                      <a onClick={() => previewButtonHandler(photographUrl)}>
                        <Box
                          component="img"
                          className="image d-block m-auto cursor-pointer"
                          alt="Image"
                          src={getImageUrl(photographUrl)}
                        />
                      </a>
                      <Tooltip title={name}>
                        <DocumentName fontSize={13} variant="body2">
                          {name}
                        </DocumentName>
                      </Tooltip>
                    </Col>
                  )
                )}
              </Row>
            )}
            {!!disableResolveDetailField && (
              <>
                <Row className="mt-3 mb-2">
                  <Col xs={12}>
                    <h5 className="fw-bold mb-0">
                      {t("Developer Compliance:")}
                    </h5>
                  </Col>
                </Row>
                {consumerResponseDetailField.map(
                  ({ name, key, isVisible, isRequired, renderField }) => {
                    const value = get(observationDetails, key, "");
                    if (typeof isVisible !== "boolean" || isVisible) {
                      return (
                        <Col key={key} lg={6} className="mb-1">
                          <Row>
                            <Col md={5}>{t(name)}:</Col>
                            <Col
                              md={7}
                              className="fw-semibold"
                              style={{ wordBreak: "break-word" }}
                            >
                              {renderField
                                ? renderField(value)
                                : get(observationDetails, key, null) ??
                                  (isRequired ? "-" : "N/A")}
                            </Col>
                          </Row>
                        </Col>
                      );
                    }
                  }
                )}
              </>
            )}
            <Grid container rowSpacing={1} columnSpacing={2} sx={{ pl: 1 }}>
              <Grid item xs={12} className="mb-3 ms-1 mt-1">
                <ConsumerResolveDetails
                  t={t}
                  isAdmin={isAdmin}
                  isResolved={isResolved}
                  handleClose={() => setDocumentToPreview(null)}
                  previewButtonHandler={previewButtonHandler}
                  observationDetails={observationDetails}
                />

                {isAuthorizedAdminRole &&
                  !isResolved &&
                  !!resolveImages?.length &&
                  !!disableResolveDetailField && (
                    <div className="d-flex justify-content-end mt-2">
                      <Button
                        size="medium"
                        variant="contained"
                        type="submit"
                        color="success"
                        onClick={handleApproveResolvePhotographs}
                        disabled={isCloseBuildingObservationLoading}
                      >
                        {t("Approve")}
                        {isCloseBuildingObservationLoading && (
                          <CircularProgress size={20} />
                        )}
                      </Button>
                    </div>
                  )}
              </Grid>
            </Grid>
            <>
              {((isAuthorizedAdminRole && isApproved) || !isAdmin) && (
                <>
                  <div
                    className="fs-4 fw-semibold mb-3 border-bottom"
                    onClick={handleDiscussionCollapse}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <div>{t("Developer Discussion")}</div>{" "}
                      <p>{openDiscussion ? <ExpandLess /> : <ExpandMore />}</p>
                    </Stack>
                  </div>
                  <Collapse in={openDiscussion} timeout="auto" unmountOnExit>
                    {!isResolved && (
                      <CommentInput
                        handleSubmit={addNewComment}
                        isFireSafety
                        inspectionObservationId={observationId}
                      />
                    )}

                    {getCommentsByObservationId(commentData, observationId)
                      .length ? (
                      getCommentsByObservationId(
                        commentData,
                        observationId
                      ).map(({ comment, createdAt, name }, index) => (
                        <CommentBox
                          comment={comment}
                          name={name}
                          createdAt={createdAt}
                          key={index}
                        />
                      ))
                    ) : (
                      <Card variant="outlined" className="mt-2 p-0">
                        <CardContent className="pt-1">
                          <Typography color="GrayText" gutterBottom>
                            {t("No comment")}
                          </Typography>
                        </CardContent>
                      </Card>
                    )}
                  </Collapse>
                </>
              )}

              {isAdmin && (
                <AdminDiscussionForBuildingObservation
                  inspectionObservationId={observationId}
                  adminComments={getCommentsByObservationId(
                    adminComments,
                    observationId
                  )}
                  isResolved={isResolved}
                />
              )}
            </>
          </Row>
        </Collapse>
      </>
    </>
  );
};

FireSafetyViewBuildingObservationDetails.propTypes = {
  observationDetails: PropTypes.object,
  index: PropTypes.number,
  buildingInformationId: PropTypes.number,
  t: PropTypes.func,
};

export default withTranslation()(FireSafetyViewBuildingObservationDetails);
